import { ApplicationRef, computed, DestroyRef, inject, Injectable, Injector, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { concat, filter, firstValueFrom, fromEvent, interval, map, merge, take } from "rxjs";
import { Constants } from "../../constants/network";
import { ConnectivityService } from "../network/services/connectivity.service";
import { clearCachedAppConfigs } from "../config/config.service";
import { IndexedDbService } from "../storage/indexed-db.service";

/**
 * Detects a newly deployed version and activates it on request.
 *
 * Drives the update state of `app-pwa-banner`, which is deliberately non-blocking — a field user
 * must never be interrupted mid-inspection. Dismissal is per-version: dismissing hides the banner
 * for the version currently offered, but a later deployment shows it again.
 *
 * @see Docs/pwa-app-upgrade-guide.md §5
 */
@Injectable({ providedIn: "root" })
export class PwaUpdateService {
  /**
   * Optional so the service stays constructible without `provideServiceWorker`. It is reached from
   * `app-header`, hence from nearly every page, so a hard dependency would force every component
   * test that renders a page to provide SwUpdate. An absent SwUpdate behaves like a disabled one:
   * the service goes inert and the banner renders nothing.
   */
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly appRef = inject(ApplicationRef);
  private readonly connectivity = inject(ConnectivityService);
  private readonly destroyRef = inject(DestroyRef);
  /**
   * Resolved lazily (only inside `clearLocalDataForUpdate()`) rather than injected eagerly as a
   * field: eager injection would construct `IndexedDbService`, which requires `INDEXED_DB_CONFIG`,
   * on every construction of this service — including the many component tests that render
   * `app-header` (hence this service) without providing that token.
   */
  private readonly injector = inject(Injector);

  /** Version waiting to be activated, and the one the user last dismissed. */
  private readonly readyVersion = signal<string | null>(null);
  private readonly dismissedVersion = signal<string | null>(null);

  /** True from the moment "Update" is pressed until the page reloads. */
  readonly activating = signal(false);

  /** Set when the service worker reports a corrupt cache; only a reload clears it. */
  readonly unrecoverable = signal(false);

  /** A new version is waiting and has not been dismissed. Drives the banner. */
  readonly updateAvailable = computed(() => {
    const ready = this.readyVersion();
    return ready !== null && ready !== this.dismissedVersion();
  });

  constructor() {
    if (!this.swUpdate?.isEnabled) {
      // The local "development" build configuration sets no "serviceWorker" (see environment.ts),
      // so there is nothing to poll.
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event): event is VersionReadyEvent => event.type === "VERSION_READY")
      )
      .subscribe(event => this.readyVersion.set(event.latestVersion.hash));

    this.swUpdate.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.unrecoverable.set(true));

    this.startPolling();
  }

  /**
   * Activates the waiting version, clears the local IndexedDB cache, then reloads to the app root.
   * The reload is mandatory: the running bundle belongs to the previous version, so continuing
   * without it mixes old code against a new cache.
   *
   * Every update wipes local data — including any not-yet-synced `offline-transactions` — rather
   * than trying to guess whether this particular deployment changed the schema/business logic, so
   * `pwa.update.bannerMessage` warns the user to submit their offline work first. Navigating to the
   * root (instead of reloading the current URL) guarantees the next boot renders `Home`, whose
   * `equipment.length === 0` check then drives the existing `/data-loading` preload flow instead of
   * resuming a deep route against data that no longer exists.
   */
  async applyUpdate(): Promise<void> {
    if (!this.swUpdate || this.activating() || this.readyVersion() === null) {
      return;
    }
    this.activating.set(true);
    try {
      if (await this.swUpdate.activateUpdate()) {
        await this.clearLocalDataForUpdate();
        this.reloadPage();
        return;
      }
      // Resolved false: nothing was waiting after all.
      this.readyVersion.set(null);
    } catch {
      // Leave the banner up so the user can retry; a manual reload also picks the new version up.
    }
    this.activating.set(false);
  }

  /** Hides the banner for the currently offered version only. */
  dismiss(): void {
    this.dismissedVersion.set(this.readyVersion());
  }

  /** Escape hatch from an unrecoverable cache state. */
  reloadForUnrecoverableState(): void {
    this.reloadPage();
  }

  /**
   * Clears local caches before the update reload (see `applyUpdate()`'s docstring).
   * `IndexedDbService` is resolved here, not as a field (see its injection point's docstring).
   * Failure is logged, surfaced to the user via a toast, and swallowed rather than blocking the
   * update — an update that proceeds with stale local data is preferable to one that never applies
   * at all.
   */
  private async clearLocalDataForUpdate(): Promise<void> {
    try {
      clearCachedAppConfigs();
      const indexedDbService = this.injector.get(IndexedDbService);
      await firstValueFrom(indexedDbService.clearAllStores());
    } catch (err) {
      console.error("[PwaUpdateService] Failed to clear local data before update reload", err);
    }
  }

  /**
   * Single seam for both reload paths, so tests can stub it. Navigates to the app root rather than
   * reloading the current URL, so the update path always lands on `Home` (see `applyUpdate()`'s
   * docstring); `reloadForUnrecoverableState()` reuses this too, since a corrupt cache is just as
   * safely recovered from the root as from whatever route triggered it.
   */
  private reloadPage(): void {
    location.assign("/");
  }

  /**
   * Poll triggers, in order of real-world usefulness on a field device:
   *   1. tab becomes visible   — the device spends most of its life backgrounded
   *   2. connectivity restored — most checks would otherwise land while offline
   *   3. every 30 minutes      — backstop for a device left open and online
   *
   * Gated behind ApplicationRef.isStable so the first check never competes with initial rendering
   * or the app initializer's config/i18n load.
   */
  private startPolling(): void {
    // filter + take rather than first(predicate): if isStable ever completed without emitting true,
    // first() would throw EmptyError into an unhandled subscription. This form completes silently,
    // which for a background poller is the correct failure mode.
    const appStable$ = this.appRef.isStable.pipe(
      filter(stable => stable),
      take(1)
    );
    const tabVisible$ = fromEvent(document, "visibilitychange").pipe(filter(() => !document.hidden));
    const backOnline$ = fromEvent(window, "online");

    concat(appStable$, merge(interval(Constants.Pwa.UpdateCheckIntervalMs), tabVisible$, backOnline$))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(() => undefined)
      )
      .subscribe(() => void this.checkForUpdate());
  }

  private async checkForUpdate(): Promise<void> {
    if (!this.swUpdate || !this.connectivity.isOnline()) {
      return;
    }
    try {
      await this.swUpdate.checkForUpdate();
    } catch {
      // Transient network failure — the next trigger retries.
    }
  }
}
